if (!customElements.get('facet-form')) {
  customElements.define(
    'facet-form',
    class FacetForm extends HTMLFormElement {
      constructor() {
        super();

        this.dirty = false;
        this.cachedMap = new Map();
        this.isMobile = theme.config.mqlSmall || theme.config.isTouch;
        this.motionReduced = theme.config.motionReduced || this.hasAttribute('motion-reduced');

        this.addEventListener('change', this.onFormChange);
        this.addEventListener('submit', this.onFormSubmit);
      }

      getAnimationParams() {
        return {
          distance: this.isMobile ? 30 : 50,
          duration: this.motionReduced ? 0 : (this.isMobile ? 0.3 : 0.5),
          staggerDelay: this.motionReduced ? 0 : (this.isMobile ? 0.05 : 0.1)
        };
      }

      getVisibleItems(items) {
        const viewportHeight = window.innerHeight + window.scrollY;
        const visible = [];
        const invisible = [];
        
        items.forEach(item => {
          const rect = item.getBoundingClientRect();
          const isVisible = rect.top < viewportHeight && rect.bottom > 0;
          
          if (isVisible) {
            visible.push(item);
          } else {
            invisible.push(item);
          }
        });
        
        return { visible, invisible };
      }

      onFormChange() {
        this.dirty = true;
        this.dispatchEvent(new Event('submit', { cancelable: true }));
      }

      onFormSubmit(event) {
        event.preventDefault();
        if (!this.dirty) return;

        const url = this.buildUrl().toString();
        console.log('Form submitting with URL:', url);
        this.renderSection(url, event);
      }

      buildUrl() {
        const formData = new FormData(this);
        const searchParams = new URLSearchParams();
        // Start with a clean URL - use the base collection URL without existing search params
        // This ensures old filter parameters are removed when checkboxes are unchecked
        const url = new URL(this.action);
        url.search = ''; // Clear existing search params

        // Debug: Log all form data
        console.log('FormData entries:');
        for (const [key, value] of formData.entries()) {
          console.log(`  ${key}: ${value}`);
        }

        // Price filter values should be in whole currency units (not cents)
        // Track price filter keys and values
        let minPriceKey = null;
        let maxPriceKey = null;
        let minPriceValue = null;
        let maxPriceValue = null;
        
        formData.forEach((value, key) => {
          // Check if this is a price filter parameter (gte = greater than or equal, lte = less than or equal)
          if (key.includes('price.gte') || key.includes('price.lte')) {
            // Keep as whole currency number (no conversion to cents)
            // Only process if value exists and is a valid number (not empty, not placeholder-like values)
            const trimmedValue = value ? value.toString().trim() : '';
            console.log(`Processing price filter ${key}: "${trimmedValue}"`);
            // Check if it's a real value (not empty, not undefined/null strings, not placeholder)
            if (trimmedValue !== '' && trimmedValue !== 'undefined' && trimmedValue !== 'null') {
              const currencyValue = parseFloat(trimmedValue);
              if (!isNaN(currencyValue) && currencyValue >= 0) {
                // Round to whole number (no decimals, no cents conversion)
                const wholeValue = Math.round(currencyValue);
                console.log(`  Converted to: ${wholeValue}`);
                if (key.includes('price.gte')) {
                  minPriceKey = key;
                  minPriceValue = wholeValue;
                } else if (key.includes('price.lte')) {
                  maxPriceKey = key;
                  maxPriceValue = wholeValue;
                }
              }
            }
          } else {
            // For all other fields (including checkboxes), add them as-is
            // FormData only contains checked checkboxes, which is correct behavior
            // When a checkbox is unchecked, it's not in FormData, so it won't be added to URL (correct)
            if (key.includes('availability')) {
              console.log(`Processing availability filter: ${key}=${value}`);
            }
            searchParams.append(key, value);
          }
        });
        
        // Add price filters if they have valid values
        // Both min and max can be added together: ?filter.v.price.gte=minprice&filter.v.price.lte=maxprice
        // Get the original range_max from the price-range element (this is in cents from Shopify, convert to currency units)
        const priceRangeElement = this.querySelector('price-range');
        const originalRangeMaxCents = priceRangeElement ? parseInt(priceRangeElement.getAttribute('data-range-max')) : null;
        const originalRangeMaxCurrency = originalRangeMaxCents ? Math.round(originalRangeMaxCents / 100) : null;
        console.log(`Original range_max: ${originalRangeMaxCents} cents = ${originalRangeMaxCurrency} currency units`);
        
        // Add min price filter (filter.v.price.gte) if it's >= 0 (0 is valid)
        // Min and max are independent - both can be present in the URL when both are set
        if (minPriceKey && minPriceValue !== null && minPriceValue >= 0) {
          console.log(`Adding min price filter: ${minPriceKey}=${minPriceValue}`);
          searchParams.append(minPriceKey, minPriceValue.toString());
        }
        
        // Add max price filter (filter.v.price.lte) if it's > 0 and less than the maximum possible range
        // If maxPriceValue equals or is very close to originalRangeMaxCurrency, it means "no filter" - show all products
        if (maxPriceKey && maxPriceValue !== null && maxPriceValue > 0) {
          console.log(`Max price value: ${maxPriceValue}, originalRangeMaxCurrency: ${originalRangeMaxCurrency}`);
          if (originalRangeMaxCurrency) {
            // Only submit if the value is less than the original range_max (in currency units)
            // Use tolerance of 1 currency unit to account for rounding differences
            if (maxPriceValue < originalRangeMaxCurrency) {
              console.log(`Adding max price filter: ${maxPriceKey}=${maxPriceValue}`);
              searchParams.append(maxPriceKey, maxPriceValue.toString());
            } else {
              console.log(`Skipping max price filter: ${maxPriceValue} >= ${originalRangeMaxCurrency} (no filter)`);
            }
            // If maxPriceValue equals or exceeds originalRangeMaxCurrency, don't submit it (no max filter)
          } else {
            // If we don't have range_max info, submit it (fallback behavior)
            console.log(`Adding max price filter (no range_max info): ${maxPriceKey}=${maxPriceValue}`);
            searchParams.append(maxPriceKey, maxPriceValue.toString());
          }
        } else {
          console.log(`Not adding max price filter: maxPriceKey=${maxPriceKey}, maxPriceValue=${maxPriceValue}`);
        }

        // Convert searchParams to URL searchParams
        url.search = '';
        searchParams.forEach((value, key) => url.searchParams.append(key, value));

        // Clean up empty parameters (but don't remove valid price filters)
        // Price filters use whole currency numbers (not cents)
        url.searchParams.forEach((value, key) => {
          if (key.includes('price.gte')) {
            // For min price, only remove if it's actually empty/null/undefined (0 is valid for "from 0")
            const paramValue = url.searchParams.get(key);
            if (!paramValue || paramValue === '' || paramValue === 'null' || paramValue === 'undefined') {
              url.searchParams.delete(key);
            }
          } else if (key.includes('price.lte')) {
            // For max price, remove if empty/null/undefined/zero (0 means no max filter)
            const paramValue = url.searchParams.get(key);
            if (!paramValue || paramValue === '' || paramValue === '0' || paramValue === 'null' || paramValue === 'undefined') {
              url.searchParams.delete(key);
            }
          } else if (key === 'page') {
            // Remove empty page parameter
            const paramValue = url.searchParams.get(key);
            if (!paramValue || paramValue === '' || paramValue === '0') {
              url.searchParams.delete(key);
            }
          }
        });

        url.searchParams.set('section_id', theme.utils.sectionId(this));
        console.log('Final URL:', url.toString());
        return url;
      }

      updateURLHash(url) {
        const clonedUrl = new URL(url);
        clonedUrl.searchParams.delete('section_id');
        history.replaceState({}, '', clonedUrl.toString());
      }

      beforeRenderSection() {
        console.log('beforeRenderSection called');
        const container = document.getElementById('ProductGridContainer');
        if (!container) {
          console.warn('ProductGridContainer not found in beforeRenderSection');
          return;
        }
        const items = container.querySelectorAll('.product-card, .card');
        console.log('beforeRenderSection found items:', items.length);

        const { distance, duration, staggerDelay } = this.getAnimationParams();
        const { visible: visibleItems, invisible: inVisibleItems } = this.getVisibleItems(items);

        Motion.timeline([
          [visibleItems, { y: distance, opacity: 0, visibility: 'hidden' }, { duration: duration, delay: Motion.stagger(staggerDelay) }],
          [inVisibleItems, { y: distance, opacity: 0, visibility: 'hidden' }, { duration: duration }],
          [container, { y: distance, opacity: 0 }, { duration: duration, easing: 'linear' }]
        ]).finished.then(() => {
          console.log('beforeRenderSection animation completed');
        });
        items.forEach(item => item.style.removeProperty('transform'));

        setTimeout(() => {
          const target = document.querySelector('.collection');
          window.scrollTo({
            top: target.getBoundingClientRect().top + window.scrollY - (theme.config.isTouch || theme.config.mqlSmall ? 95 : 20),
            behavior: theme.config.motionReduced ? 'auto' : 'smooth'
          });

          const drawer = document.getElementById('FacetDrawer');
          if (drawer) drawer.classList.add('loading');
        }, 100);
      }

      afterRenderSection() {
        console.log('afterRenderSection called');
        const container = document.getElementById('ProductGridContainer');
        if (!container) {
          console.warn('ProductGridContainer not found in afterRenderSection');
          return;
        }
        
        // Query for items - check both .product-card and .card (products might use either class)
        const items = container.querySelectorAll('.product-card, .card');
        console.log('afterRenderSection found items:', items.length);
        
        const { distance, duration, staggerDelay } = this.getAnimationParams();
        console.log('Animation params:', { distance, duration, staggerDelay }, 'motionReduced:', this.motionReduced);
        
        // Check for empty state message in container
        const emptyState = container.querySelector('.empty-state');
        if (items.length === 0) {
          console.warn('No product cards found in afterRenderSection');
          if (emptyState) {
            console.log('Empty state message found in container');
          }
          // Still ensure container is visible even if no items (might have empty state message)
          container.style.opacity = '1';
          container.style.removeProperty('visibility');
          container.style.removeProperty('transform');
          
          // Make empty state visible if it exists
          if (emptyState) {
            emptyState.style.display = '';
            emptyState.style.visibility = 'visible';
            emptyState.style.opacity = '1';
          }
          
          const drawer = document.getElementById('FacetDrawer');
          if (drawer) drawer.classList.remove('loading');
          document.dispatchEvent(new CustomEvent('collection:reloaded'));
          return;
        }

        const { visible: visibleItems, invisible: inVisibleItems } = this.getVisibleItems(items);
        console.log('Visible items:', visibleItems.length, 'Invisible items:', inVisibleItems.length);

        // If motion is reduced (duration is 0), skip animation and make items visible immediately
        // Also check this.motionReduced explicitly as a fallback
        if (duration === 0 || this.motionReduced || theme?.config?.motionReduced) {
          console.log('Motion reduced - making items visible immediately');
          container.style.opacity = '1';
          container.style.removeProperty('visibility');
          container.style.removeProperty('transform');
          items.forEach(item => {
            item.style.visibility = 'visible';
            item.style.opacity = '1';
            item.style.removeProperty('transform');
          });
          console.log('Items set to visible, count:', items.length);
        } else {
          // Ensure items start from hidden state for animation
          items.forEach(item => {
            item.style.visibility = 'hidden';
            item.style.opacity = '0';
          });
          console.log('Set items to hidden state for animation');

          // Ensure container starts at the correct state for animation
          container.style.removeProperty('visibility');

          Motion.timeline([
            [container, { y: [distance, 0], opacity: [0, 1] }, { duration: duration, easing: 'linear' }],
            [visibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration, delay: Motion.stagger(staggerDelay) }],
            [inVisibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration }]
          ]).finished.then(() => {
            console.log('Animation completed, checking final state');
            const firstItem = items[0];
            if (firstItem) {
              const style = window.getComputedStyle(firstItem);
              console.log('First item after animation - visibility:', style.visibility, 'opacity:', style.opacity, 'display:', style.display);
            }
            const containerStyle = window.getComputedStyle(container);
            console.log('Container after animation - opacity:', containerStyle.opacity);
          }).catch(err => {
            console.error('Animation error:', err);
            // Force visibility on error
            container.style.opacity = '1';
            items.forEach(item => {
              item.style.visibility = 'visible';
              item.style.opacity = '1';
            });
          });
        }
        
        items.forEach(item => item.style.removeProperty('transform'));

        const drawer = document.getElementById('FacetDrawer');
        if (drawer) drawer.classList.remove('loading');

        document.dispatchEvent(new CustomEvent('collection:reloaded'));
        console.log('afterRenderSection completed');
      }

      renderSection(url, event) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url, event)
          : this.renderSectionFromFetch(url, event);

        if (this.hasAttribute('data-history')) this.updateURLHash(url);

        this.dirty = false;
      }

      renderSectionFromFetch(url, event) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        
        this.beforeRenderSection();
        const start = performance.now();

        console.log('Fetching URL:', url);
        fetch(url, { signal: this.abortController.signal })
          .then((response) => {
            console.log('Response status:', response.status);
            return response.text();
          })
          .then((responseText) => {
            const execution = (performance.now() - start);
            console.log('Response received, length:', responseText.length);
            console.log('Response preview:', responseText.substring(0, 500));

            setTimeout(() => {
              this.renderFilters(responseText, event);
              this.renderFiltersActive(responseText);
              this.renderProductGridContainer(responseText);
              this.renderProductCount(responseText);
              this.renderSortBy(responseText);

              theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });
              this.cachedMap.set(url, responseText);

              console.log('Calling afterRenderSection');
              this.afterRenderSection();
            }, execution > 500 ? 0 : 500);
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error('Fetch error:', error);
            }
          });
      }

      renderSectionFromCache(url, event) {
        this.beforeRenderSection();

        setTimeout(() => {
          const responseText = this.cachedMap.get(url);
          this.renderFilters(responseText, event);
          this.renderFiltersActive(responseText);
          this.renderProductGridContainer(responseText);
          this.renderProductCount(responseText);
          this.renderSortBy(responseText);

          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });

          this.afterRenderSection();
        }, 500);
      }

      renderFilters(responseText, event) {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const facetElements = parsedHTML.querySelectorAll(
          '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter]'
        );

        const matchesIndex = (element) => {
          const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
          return jsFilter ? element.getAttribute('data-index') === jsFilter.getAttribute('data-index') : false;
        };
        const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

        facetsToRender.forEach((element) => {
          const filter = document.querySelector(`[data-filter][data-index="${element.getAttribute('data-index')}"]`);
          if (filter !== null) {
            if (filter.tagName === 'DETAILS') {
              filter.querySelector('summary + *').innerHTML = element.querySelector('summary + *').innerHTML;
            }
            else {
              filter.innerHTML = element.innerHTML;
            }
          }
        });
      }

      renderFiltersActive(responseText) {
        const id = 'FacetFiltersActive';
        if (document.getElementById(id) === null) return;
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

        document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;
      }

      renderProductGridContainer(responseText) {
        const id = 'ProductGridContainer';
        const container = document.getElementById(id);
        if (container === null) {
          console.warn('ProductGridContainer not found in document');
          return;
        }
        
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        
        // Shopify section responses might be wrapped in .shopify-section
        // Try to find ProductGridContainer directly first, then within shopify-section
        let sourceContainer = parsedHTML.getElementById(id);
        if (sourceContainer === null) {
          const shopifySection = parsedHTML.querySelector('.shopify-section');
          if (shopifySection) {
            sourceContainer = shopifySection.querySelector('#' + id);
          }
        }
        
        if (sourceContainer === null) {
          console.warn('ProductGridContainer not found in response');
          console.log('Available IDs in response:', Array.from(parsedHTML.querySelectorAll('[id]')).map(el => el.id));
          console.log('Response HTML structure:', parsedHTML.body.innerHTML.substring(0, 1000));
          return;
        }
        
        console.log('Found ProductGridContainer, innerHTML length:', sourceContainer.innerHTML.length);
        console.log('ProductGridContainer content preview:', sourceContainer.innerHTML.substring(0, 500));
        
        // Check if there's an empty state message or no products message
        const emptyStateMessage = sourceContainer.querySelector('[data-empty-state], .empty-state, .no-products');
        if (emptyStateMessage) {
          console.log('Empty state message found:', emptyStateMessage.textContent?.substring(0, 100));
        }
        
        // Set motion-reduced attribute on motion-list if it exists in source
        const sourceMotionList = sourceContainer.querySelector('motion-list') || parsedHTML.querySelector('motion-list');
        if (sourceMotionList) {
          sourceMotionList.setAttribute('motion-reduced', '');
        }

        // Replace the entire innerHTML (not append)
        container.innerHTML = sourceContainer.innerHTML;
        
        console.log('Updated ProductGridContainer, new innerHTML length:', container.innerHTML.length);
        const cards = container.querySelectorAll('.product-card, .card');
        console.log('Product cards found:', cards.length);
        
        // Check what's actually in the container after update
        if (cards.length === 0) {
          console.log('Container HTML structure:', container.innerHTML.substring(0, 1000));
          const motionList = container.querySelector('motion-list');
          if (motionList) {
            console.log('motion-list found, innerHTML length:', motionList.innerHTML.length);
            console.log('motion-list content:', motionList.innerHTML.substring(0, 500));
          }
        }
        
        // Note: afterRenderSection() will handle animating these items in
        // We don't need to make them visible here as the animation will do that
      }

      renderProductCount(responseText) {
        const id = 'ProductCount';
        if (document.getElementById(id) === null) return;
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

        document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;
      }

      renderSortBy(responseText) {
        const id = 'SortByContainer';
        if (document.getElementById(id) === null) return;
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

        document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('facet-count')) {
  customElements.define(
    'facet-count',
    class FacetCount extends HTMLElement {
      constructor() {
        super();
      }

      facetUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.facetUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, this.onFacetUpdate.bind(this));
      }

      disconnectedCallback() {
        if (this.facetUpdateUnsubscriber) {
          this.facetUpdateUnsubscriber();
        }
      }

      get itemCount() {
        return parseInt(this.innerText);
      }

      onFacetUpdate(event) {
        const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
        const facetCount = parsedHTML.querySelector('facet-count');
        this.innerText = facetCount.innerHTML;
        this.hidden = this.itemCount === 0;
      }
    }
  );
}

if (!customElements.get('facet-remove')) {
  customElements.define(
    'facet-remove',
    class FacetRemove extends MagnetLink {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick(event) {
        const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

        if (form) {
          event.preventDefault();

          const url = new URL(this.href);
          url.searchParams.set('section_id', theme.utils.sectionId(form));
          form.renderSection(url.toString(), event);
        }
      }
    }, { extends: 'a' }
  );
}

if (!customElements.get('facet-sort')) {
  customElements.define(
    'facet-sort',
    class FacetSort extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });

        this.addEventListener('change', this.onChange);
        this.button.addEventListener('click', this.show.bind(this));
        this.close.addEventListener('click', this.hide.bind(this));
        document.addEventListener('click', this.onWindowClick.bind(this));
      }

      get listbox() {
        return this.querySelector('.sort-listbox');
      }

      get selection() {
        return this.querySelector('.sort-selection');
      }

      get button() {
        return this.querySelector('.sort-by');
      }

      get close() {
        return this.querySelector('.sort-close');
      }

      init() {
        this.initButton();
        this.style.setProperty('--facet-listbox-height', `${this.listbox.getBoundingClientRect().height}px`);
      }

      initButton() {
        const value = this.selection.innerText;
        const width = theme.getElementWidth(this.selection, value);
        this.style.setProperty('--facet-button-width', `${width}px`);
      }

      onChange(event) {
        const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

        if (form) {
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', event.target.value);
          url.searchParams.set('section_id', theme.utils.sectionId(form));
          url.searchParams.delete('page');
          form.renderSection(url.toString(), event);
        }

        this.selection.innerText = event.target.nextElementSibling.innerText;
        this.initButton();
        this.hide();
      }

      show() {
        this.button.setAttribute('open', '');
      }

      hide(immediate = true) {
        if (this.button.hasAttribute('open')) {
          setTimeout(() => {
            this.button.removeAttribute('open');
          }, 100);

          if (theme.config.isTouch || document.body.getAttribute('data-button-hover') === 'none') return;
          
          const btnFill = this.button.querySelector('[data-fill');
          Motion.animate(btnFill, { y: ['0%', immediate ? '0%' : '-76%'] }, { duration: 0.6, delay: immediate ? 0 : 0.2 });
        }
      }

      onWindowClick(event) {
        if (!this.contains(event.target)) {
          if (this.button.hasAttribute('open')) {
            this.hide(false);
          }
        }
      }
    }
  );
}

if (!customElements.get('facet-sticky')) {
  customElements.define(
    'facet-sticky',
    class FacetSticky extends HTMLElement {
      constructor() {
        super();

        new IntersectionObserver(this.handleIntersection.bind(this), { rootMargin: `-${screen.availHeight - 100}px 0px ${screen.availHeight}px 0px` }).observe(document.querySelector('.collection-section'));
      }

      get button() {
        return this.querySelector('.button');
      }

      handleIntersection(entries) {
        if (entries[0].isIntersecting) {
          Motion.animate(this.button, { opacity: 1, visibility: 'visible', transform: ['translateY(15px)', 'translateY(0)'] }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
        }
        else {
          Motion.animate(this.button, { opacity: 0, visibility: 'hidden', transform: ['translateY(0)', 'translateY(15px)'] }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
        }
      }
    }
  );
}

if (!customElements.get('price-range')) {
  customElements.define(
    'price-range',
    class PriceRange extends HTMLElement {
      constructor() {
        super();

        this.rangeMin = this.querySelector('input[type="range"]:first-child');
        this.rangeMax = this.querySelector('input[type="range"]:last-child');
        // Find inputs by name pattern (Shopify price filters use filter.v.price.gte/lte)
        this.inputMin = this.querySelector('input[name*="price.gte"], input[name*="filter.v.price.gte"]');
        this.inputMax = this.querySelector('input[name*="price.lte"], input[name*="filter.v.price.lte"]');
        
        if (!this.inputMin || !this.inputMax) {
          // Fallback: find by position if name selector fails
          const priceInputs = this.querySelectorAll('input[type="number"]');
          if (priceInputs.length >= 2) {
            this.inputMin = priceInputs[0];
            this.inputMax = priceInputs[1];
          }
        }

        // Get the original range_max in cents from data attribute, convert to currency units
        const originalRangeMaxCents = this.getAttribute('data-range-max') ? parseInt(this.getAttribute('data-range-max')) : null;
        const originalRangeMaxCurrency = originalRangeMaxCents ? Math.round(originalRangeMaxCents / 100) : null;
        
        // On initialization, clear max input if it equals the full range (means "no filter")
        // This prevents submitting the filter when the input shows the maximum price (which means "no filter")
        if (this.inputMax && originalRangeMaxCurrency) {
          const maxInputValue = parseFloat(this.inputMax.value);
          if (!isNaN(maxInputValue)) {
            const maxInputValueCurrency = Math.round(maxInputValue);
            const maxInputMax = parseFloat(this.inputMax.max);
            
            // Clear the input if:
            // 1. The value equals or exceeds the max attribute, OR
            // 2. The value equals the original range_max (no filter)
            if (maxInputValueCurrency >= maxInputMax || maxInputValueCurrency >= originalRangeMaxCurrency) {
              this.inputMax.value = '';
            }
          }
        }

        // Only attach event listeners if inputs are found
        if (this.inputMin && this.inputMax) {
          this.inputMin.addEventListener('focus', () => this.inputMin.select());
          this.inputMax.addEventListener('focus', () => this.inputMax.select());
          this.inputMin.addEventListener('change', this.onInputMinChange.bind(this));
          this.inputMax.addEventListener('change', this.onInputMaxChange.bind(this));
        }

        if (this.rangeMin) {
          this.rangeMin.addEventListener('change', this.onRangeMinChange.bind(this));
          this.rangeMin.addEventListener('input', this.onRangeMinInput.bind(this));
        }
        if (this.rangeMax) {
          this.rangeMax.addEventListener('change', this.onRangeMaxChange.bind(this));
          this.rangeMax.addEventListener('input', this.onRangeMaxInput.bind(this));
        }
      }

      onInputMinChange(event) {
        const inputValue = parseFloat(event.target.value);
        if (isNaN(inputValue)) {
          event.target.value = event.target.min || '0';
          // Trigger input event to ensure form sees the change
          event.target.dispatchEvent(new Event('input', { bubbles: true }));
          return;
        }
        const minVal = inputValue;
        const maxInputVal = parseFloat(this.inputMax.value);
        const maxAllowed = isNaN(maxInputVal) ? parseFloat(event.target.max) || 0 : maxInputVal;
        const minAllowed = parseFloat(event.target.min) || 0;
        const constrainedValue = Math.max(Math.min(minVal, maxAllowed), minAllowed);
        // Set the constrained value as a string to ensure it's properly set
        event.target.value = constrainedValue.toString();
        // Range slider uses integer values (rounded)
        const rangeValue = Math.round(constrainedValue);
        if (this.rangeMin) {
          this.rangeMin.value = rangeValue;
          const rangeMax = parseFloat(this.rangeMin.max) || 1;
          this.rangeMin.parentElement.style.setProperty('--range-min', `${(rangeValue / rangeMax) * 100}%`);
        }
        // Trigger input event to ensure form sees the updated value
        event.target.dispatchEvent(new Event('input', { bubbles: true }));
      }

      onInputMaxChange(event) {
        // Update the value first to ensure FormData picks it up
        const inputValue = parseFloat(event.target.value);
        if (isNaN(inputValue) || inputValue === '') {
          // Clear both input and range slider if empty
          event.target.value = '';
          if (this.rangeMax) {
            const rangeMaxVal = parseFloat(this.rangeMax.max) || 0;
            this.rangeMax.value = rangeMaxVal;
            this.rangeMax.parentElement.style.setProperty('--range-max', '100%');
          }
          // Trigger input event to ensure form sees the change
          event.target.dispatchEvent(new Event('input', { bubbles: true }));
          return;
        }
        const maxVal = inputValue;
        const minInputVal = parseFloat(this.inputMin.value);
        const minAllowed = isNaN(minInputVal) ? parseFloat(event.target.min) || 0 : minInputVal;
        const maxAllowed = parseFloat(event.target.max) || 0;
        const constrainedValue = Math.min(Math.max(maxVal, minAllowed), maxAllowed);
        
        // If the value equals the max allowed (full range), clear it (means "no filter")
        if (constrainedValue >= maxAllowed) {
          event.target.value = '';
          if (this.rangeMax) {
            this.rangeMax.value = maxAllowed;
            this.rangeMax.parentElement.style.setProperty('--range-max', '100%');
          }
          // Trigger input event to ensure form sees the change
          event.target.dispatchEvent(new Event('input', { bubbles: true }));
          return;
        }
        
        // Set the constrained value
        event.target.value = constrainedValue.toString();
        // Range slider uses integer values (rounded)
        const rangeValue = Math.round(constrainedValue);
        if (this.rangeMax) {
          this.rangeMax.value = rangeValue;
          const rangeMaxVal = parseFloat(this.rangeMax.max) || 1;
          this.rangeMax.parentElement.style.setProperty('--range-max', `${(rangeValue / rangeMaxVal) * 100}%`);
        }
        // Trigger input event to ensure form sees the updated value
        event.target.dispatchEvent(new Event('input', { bubbles: true }));
      }

      onRangeMinChange(event) {
        event.stopPropagation();
        const rangeValue = parseFloat(event.target.value) || 0;
        if (this.inputMin) {
          this.inputMin.value = rangeValue;
          this.inputMin.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      onRangeMaxChange(event) {
        event.stopPropagation();
        const rangeValue = parseFloat(event.target.value) || 0;
        const rangeMax = parseFloat(event.target.max) || 0;
        if (this.inputMax) {
          // Only set a value if it's less than the max range (meaning it's an actual filter)
          // If it equals the max range, clear the input (no max filter)
          if (rangeValue >= rangeMax) {
            this.inputMax.value = '';
          } else {
            this.inputMax.value = rangeValue;
          }
          this.inputMax.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      onRangeMinInput(event) {
        const rangeValue = parseFloat(event.target.value) || 0;
        const maxInputVal = this.inputMax ? parseFloat(this.inputMax.value) : NaN;
        const maxRange = isNaN(maxInputVal) ? parseFloat(event.target.max) || 0 : maxInputVal;
        const constrainedValue = Math.min(rangeValue, Math.round(maxRange) - 1);
        event.target.value = constrainedValue;
        const rangeMax = parseFloat(event.target.max) || 1;
        event.target.parentElement.style.setProperty('--range-min', `${(constrainedValue / rangeMax) * 100}%`);
        if (this.inputMin) {
          this.inputMin.value = constrainedValue;
        }
      }

      onRangeMaxInput(event) {
        const rangeValue = parseFloat(event.target.value) || 0;
        const minInputVal = this.inputMin ? parseFloat(this.inputMin.value) : NaN;
        const minRange = isNaN(minInputVal) ? parseFloat(event.target.min) || 0 : minInputVal;
        const rangeMax = parseFloat(event.target.max) || 1;
        const constrainedValue = Math.max(rangeValue, Math.round(minRange) + 1);
        event.target.value = constrainedValue;
        event.target.parentElement.style.setProperty('--range-max', `${(constrainedValue / rangeMax) * 100}%`);
        if (this.inputMax) {
          // Only set a value if it's less than the max range
          // If user drags to the max, clear the input (no max filter)
          if (constrainedValue >= rangeMax) {
            this.inputMax.value = '';
          } else {
            this.inputMax.value = constrainedValue;
          }
        }
      }
    }
  );
}

if (!customElements.get('infinite-button')) {
  customElements.define(
    'infinite-button',
    class InfiniteButton extends HoverButton {
      constructor() {
        super();

        this.onClickHandler = this.onClick.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.onClickHandler);

        if (this.getAttribute('mode') == 'infinite') {
          Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClickHandler);
      }

      onClick() {
        if (this.hasAttribute('aria-busy')) return;
        this.abortController?.abort();
        this.abortController = new AbortController();

        this.enableLoading();
        const url = this.buildUrl().toString();

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.renderPagination(responseText);
            this.renderProductGridContainer(responseText);
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderPagination(responseText) {
        const productGridContainer = document.getElementById('ProductGridContainer');
        if (productGridContainer === null) return;

        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = productGridContainer.querySelector('.pagination');
        const source = parsedHTML.querySelector('.pagination');

        if (source) {
          destination.innerHTML = source.innerHTML;
        }
        else {
          destination.remove();
        }
      }

      renderProductGridContainer(responseText) {
        const productGridContainer = document.getElementById('ProductGridContainer');
        if (productGridContainer === null) return;

        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = productGridContainer.querySelector('motion-list');
        const source = parsedHTML.querySelector('motion-list');

        if (source && destination) {
          source.querySelectorAll('.card').forEach((item) => {
            destination.appendChild(item);
          });

          destination.reload();
        }
      }
      
      buildUrl() {
        const url = new URL(this.getAttribute('action'));
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      enableLoading() {
        this.classList.add('pointer-events-none');
        this.setAttribute('aria-busy', 'true');
      }
    }, { extends: 'button' }
  );
}
